import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  notificationTemplatesApi,
  type TemplateLang,
  type TemplateListRow,
} from '@/api/notificationTemplates';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import {
  SERVER_LANGS,
  disabledEventCount,
  eventSwitch,
  isEmailLayoutRow,
  langState,
  rowId,
} from '@/lib/notificationTemplates';
import { cn } from '@/lib/utils';

import { ApiErrorText } from './notices';

/**
 * Матрица «событие × язык»: 36 строк на три языка. Клик по ячейке открывает
 * карточку этого языка.
 *
 * ⚠️ ЗАЧЕМ ИМЕННО МАТРИЦА. Главный вопрос админа сейчас — «где ещё не открыт
 * казахский»: тексты заехали неопубликованными и ждут сверки носителем языка.
 * В списке, где язык спрятан внутрь строки, ответ приходится собирать по
 * одному; в матрице он виден одним взглядом по колонке.
 */
export function Matrix({ onOpen }: { onOpen: (row: TemplateListRow, lang: TemplateLang) => void }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const q = useQuery({
    queryKey: ['admin', 'notification-templates'],
    queryFn: () => notificationTemplatesApi.list(),
  });

  // ⚠️ `?? []` БЕЗ useMemo ДАЁТ НОВЫЙ МАССИВ НА КАЖДЫЙ РЕНДЕР, и оба useMemo ниже
  // пересчитываются впустую. Линтер поймал это сразу — тот же случай, что с
  // перемешиванием пар в блоке сопоставления 17.08.2026.
  const rows = useMemo(() => q.data?.items ?? [], [q.data]);
  const needle = search.trim().toLowerCase();
  const shown = useMemo(
    () => rows.filter((r) => !needle || `${r.key} ${r.variant ?? ''}`.toLowerCase().includes(needle)),
    [rows, needle],
  );

  // Сколько строк ещё не опубликовано на каждом языке — та самая сводка, ради
  // которой матрица и нужна.
  const notPublished = useMemo(() => {
    const out: Record<string, number> = {};
    for (const lang of SERVER_LANGS) {
      out[lang] = rows.filter((r) => !langState(r, lang)?.published).length;
    }
    return out;
  }, [rows]);

  // Выключенные события (§6а) — по ключу: варианты делят один выключатель.
  const disabledCount = useMemo(() => disabledEventCount(rows), [rows]);

  return (
    <Card className="space-y-3" data-templates-matrix>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('adminNotificationTemplates.search')}
            spellCheck={false}
          />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {SERVER_LANGS.map((lang) => (
            <Pill key={lang} variant={notPublished[lang] ? 'warning' : 'success'}>
              {t('adminNotificationTemplates.notPublished', {
                lang: lang.toUpperCase(),
                count: notPublished[lang] ?? 0,
              })}
            </Pill>
          ))}
          {disabledCount ? (
            <Pill variant="neutral" data-templates-disabled-count>
              {t('adminNotificationTemplates.disabledCount', { count: disabledCount })}
            </Pill>
          ) : null}
        </div>
      </div>

      {q.isLoading ? <p className="text-sm text-on-surface-variant">{t('common.loading')}</p> : null}
      {q.isError ? <ApiErrorText error={q.error} /> : null}

      {shown.length ? (
        <div className="overflow-x-auto">
          {/* ⚠️ ШИРИНА ПОДОБРАНА ПОД 390 px. При `min-w-[520px]` на телефоне была
              видна только колонка RU, а остальные уезжали за прокрутку — то есть
              главный вопрос экрана («где ещё не открыт казахский») требовал
              горизонтального листания. Найдено на кадре 390. */}
          <table className="w-full min-w-[288px] text-left text-sm">
            <thead className="border-b border-outline-variant/40 text-xs uppercase tracking-label text-on-surface-variant">
              <tr>
                <th className="pb-2 pr-3">{t('adminNotificationTemplates.colEvent')}</th>
                {SERVER_LANGS.map((lang) => (
                  <th key={lang} className="pb-2 pr-1 text-center">
                    {lang.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={rowId(row)} data-template-row={rowId(row)} className="border-b border-outline-variant/20 last:border-0">
                  <td className="py-2 pr-3">
                    <span className="block break-all font-mono text-xs text-on-surface">{row.key}</span>
                    <span className="text-xs text-on-surface-variant">
                      {isEmailLayoutRow(row)
                        ? t('adminNotificationTemplates.emailLayoutRow')
                        : row.variant
                          ? t('adminNotificationTemplates.variant', { name: row.variant })
                          : t('adminNotificationTemplates.mainVariant')}
                      {row.channels?.length ? ` · ${row.channels.join(', ')}` : ''}
                    </span>
                    {/* Выключенное событие не уходит никому (§6а) — видно прямо в матрице. */}
                    {eventSwitch(row).disabled ? (
                      <span className="mt-1 block">
                        <Pill variant="warning" data-template-disabled>
                          {t('adminNotificationTemplates.disabledPill')}
                        </Pill>
                      </span>
                    ) : null}
                  </td>
                  {SERVER_LANGS.map((lang) => {
                    const st = langState(row, lang);
                    return (
                      <td key={lang} className="py-2 pr-1 text-center">
                        <button
                          type="button"
                          data-template-cell={`${rowId(row)}|${lang}`}
                          onClick={() => onOpen(row, lang)}
                          className={cn(
                            'inline-flex min-w-[52px] items-center justify-center gap-1 rounded-pill px-2 py-1 text-xs font-semibold transition',
                            st?.published
                              ? 'bg-success-container text-on-success-container'
                              : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-highest',
                          )}
                          aria-label={t('adminNotificationTemplates.openCell', { key: row.key, lang: lang.toUpperCase() })}
                        >
                          {st?.version ? `v${st.version}` : t('adminNotificationTemplates.noVersion')}
                          {st?.published ? <span aria-hidden>✓</span> : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {q.data && !shown.length ? (
        <p className="text-sm text-on-surface-variant">{t('adminNotificationTemplates.nothingFound')}</p>
      ) : null}

      <p className="text-xs text-on-surface-variant">{t('adminNotificationTemplates.publishedHint')}</p>
    </Card>
  );
}
